import React, { useState } from "react"
import moment from "moment"
import { UserInfo } from "../../../api/app.service"
import type {
  MessageResponseType,
  NextStepsValuesType,
} from "../../../models/app.interface"
import { OnSuccessfulAuthCallback } from "../context"
import { useToast } from "../../../hooks/useToast"

type View = "email" | "password" | "one-time-password"

export interface LoginFormContext {
  email: string
  setEmail: (email: string) => void

  password: string
  setPassword: (password: string) => void

  oneTimePassword: string
  setOneTimePassword: (oneTimePassword: string) => void

  view: View
  setView: (view: View) => void

  onSuccess: OnSuccessfulAuthCallback

  getSetupInfo: (userId: string) => Promise<NextStepsValuesType | undefined>
  updateTimezone: (
    userId: string,
    access_token: string
  ) => Promise<MessageResponseType | undefined>
}

export const LoginFormContext = React.createContext<LoginFormContext>(
  {} as LoginFormContext
)

export const LoginFormProvider: React.FC<
  React.PropsWithChildren<{
    defaultEmail: string
    onSuccess: OnSuccessfulAuthCallback
  }>
> = ({ children, defaultEmail, onSuccess }) => {
  const [email, setEmail] = useState<string>(defaultEmail || "")
  const [password, setPassword] = useState<string>("")
  const [oneTimePassword, setOneTimePassword] = useState<string>("")
  const [view, setView] = useState<View>("email")
  const { toast } = useToast()

  const getSetupInfo = async (userId: string) => {
    try {
      const nextSteps = await UserInfo.getNextStepsValues({
        user_id: parseInt(userId),
      })

      return nextSteps
    } catch (error: any) {
      console.error(error)
      toast.error(error.response.data.message)
    }
  }

  const updateTimezone = async (userId: string, access_token: string) => {
    try {
      const data = await UserInfo.editTimezone({
        user_id: parseInt(userId),
        access_token: access_token,
        timezone: { value: moment.tz.guess(true) },
      })

      return data
    } catch (error: any) {
      console.error(error)
      toast.error(error.response.data.message)
    }
  }

  return (
    <LoginFormContext.Provider
      value={{
        email,
        setEmail,
        password,
        setPassword,
        oneTimePassword,
        setOneTimePassword,
        view,
        setView,
        getSetupInfo,
        updateTimezone,
        onSuccess,
      }}
    >
      {children}
    </LoginFormContext.Provider>
  )
}
